import { DatePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AppModule } from '../../core/rbac/rbac.policy';
import { OpsService, AppNotification } from '../../core/services/ops.service';
import { RbacService } from '../../core/services/rbac.service';
import { ToastComponent } from '../../shared/toast/toast.component';

const CATEGORIES: Array<AppNotification['type'] | 'All'> = [
  'All',
  'Hearing',
  'Resolution',
  'Assignment',
  'Workflow',
  'System',
];

const PATH_MODULE: Array<{ prefix: string; module: AppModule }> = [
  { prefix: '/hearing-management', module: 'hearingManagement' },
  { prefix: '/resolution', module: 'resolution' },
  { prefix: '/workflow', module: 'workflow' },
  { prefix: '/scheduling', module: 'scheduling' },
  { prefix: '/roster', module: 'roster' },
  { prefix: '/cases', module: 'cases' },
  { prefix: '/reports', module: 'reports' },
  { prefix: '/assistant', module: 'assistant' },
  { prefix: '/document-ocr', module: 'documentOcr' },
  { prefix: '/settings', module: 'settings' },
  { prefix: '/roles', module: 'roles' },
  { prefix: '/resources', module: 'roles' },
  { prefix: '/complaints', module: 'complaints' },
  { prefix: '/notifications', module: 'notifications' },
  { prefix: '/profile', module: 'profile' },
  { prefix: '/dashboard', module: 'dashboard' },
];

export interface NotificationGuidance {
  nextStep: string;
  actionPath: string;
  actionLabel: string;
}

function moduleForPath(path: string): AppModule | null {
  const bare = path.split('?')[0];
  const hit = PATH_MODULE.find((p) => bare === p.prefix || bare.startsWith(`${p.prefix}/`));
  return hit?.module ?? null;
}

/** Keep deep-links usable when the signed-in desk cannot open that module. */
export function remapGuidanceForRole(
  guidance: NotificationGuidance,
  canModule: (module: AppModule) => boolean,
  grievanceRef?: string | number | null,
): NotificationGuidance {
  const module = moduleForPath(guidance.actionPath);
  if (!module || canModule(module)) return guidance;
  const encodedId =
    grievanceRef == null || grievanceRef === '' ? '' : encodeURIComponent(String(grievanceRef));
  if (canModule('complaints')) {
    return {
      ...guidance,
      actionPath: encodedId ? `/complaints/${encodedId}` : '/complaints',
      actionLabel: 'Open complaint',
      nextStep: 'Open Complaint Management to continue from your desk.',
    };
  }
  return {
    ...guidance,
    actionPath: '/dashboard',
    actionLabel: 'Go to dashboard',
    nextStep: 'This next step is not available for your desk role.',
  };
}

/**
 * The DM's inbox is scoped to Workflow handoffs the DM actually needs to act on: a
 * complaint "ready to make a case" once the investigating (record) officer submits their
 * report ("Investigation report ready for …", action "Review report"), or a brand-new case
 * just confirmed from OCR review that still needs routing (see notification-service.js's
 * 'intake_confirmed' handoff plan — "New case … created from a reviewed document").
 * We match on wording across body / next step / action label — same soft-match approach
 * for both, since neither has a dedicated field distinguishing it from other Workflow-typed
 * handoffs (e.g. approve/schedule echoes) that DM does NOT need surfaced here.
 */
export function isCaseReadyForDm(notification: AppNotification): boolean {
  if (notification.type !== 'Workflow') return false;
  const text = `${notification.body} ${notification.nextStep ?? ''} ${notification.actionLabel ?? ''}`.toLowerCase();
  return /report|reviewed document/.test(text);
}

export function notificationGuidance(notification: AppNotification): NotificationGuidance | null {
  if (notification.actionPath?.startsWith('/')) {
    return {
      nextStep: notification.nextStep || 'Open the related workspace to continue.',
      actionPath: notification.actionPath,
      actionLabel: notification.actionLabel || 'Open next step',
    };
  }

  const id = notification.grievanceRef;
  const encodedId = id == null ? '' : encodeURIComponent(String(id));
  const text = notification.body.toLowerCase();
  if (notification.type === 'Assignment') {
    return {
      nextStep: 'Open Case Management to investigate the assigned complaint and submit your report.',
      actionPath: encodedId ? `/cases?case=${encodedId}` : '/cases',
      actionLabel: 'Open case',
    };
  }
  if (notification.type === 'Hearing' && /(request|pick a slot|schedule)/.test(text) && !/scheduled/.test(text)) {
    return {
      nextStep: 'Choose a roster slot and schedule the requested hearing.',
      actionPath: encodedId ? `/scheduling?case=${encodedId}` : '/scheduling',
      actionLabel: 'Schedule hearing',
    };
  }
  if (notification.type === 'Hearing' && /(conducted|marked|complete)/.test(text)) {
    return {
      nextStep: 'Review the hearing outcome and issue the final resolution.',
      actionPath: encodedId ? `/resolution?case=${encodedId}` : '/resolution',
      actionLabel: 'Issue resolution',
    };
  }
  if (notification.type === 'Hearing') {
    return {
      nextStep: 'Review the scheduled hearing and mark it conducted after the proceedings.',
      actionPath: encodedId ? `/hearing-management?case=${encodedId}` : '/hearing-management',
      actionLabel: 'Open hearing',
    };
  }
  if (notification.type === 'Workflow' && /report/.test(text)) {
    return {
      nextStep: 'Review the investigation report and decide whether to resolve or request a hearing.',
      actionPath: encodedId ? `/resolution?case=${encodedId}` : '/resolution',
      actionLabel: 'Review report',
    };
  }
  if (notification.type === 'Workflow') {
    return {
      nextStep: 'Open Workflow Management to continue processing this complaint.',
      actionPath: encodedId ? `/workflow?case=${encodedId}` : '/workflow',
      actionLabel: 'Continue workflow',
    };
  }
  if (notification.type === 'Resolution' && encodedId) {
    return {
      nextStep: /closed/.test(text)
        ? 'No further action is required. Open the final case record if needed.'
        : 'Review the final order and complete any required follow-up.',
      actionPath: `/complaints/${encodedId}`,
      actionLabel: /closed/.test(text) ? 'View closed case' : 'View resolution',
    };
  }
  return null;
}

@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [DatePipe, ToastComponent],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.scss',
})
export class NotificationsComponent {
  private readonly ops = inject(OpsService);
  private readonly rbac = inject(RbacService);
  private readonly router = inject(Router);
  readonly filter = signal<'All' | 'Unread'>('All');
  readonly category = signal<AppNotification['type'] | 'All'>('All');
  readonly categories = CATEGORIES;
  readonly busy = signal(false);
  readonly message = signal<string | null>(null);
  readonly messageTone = signal<'success' | 'error'>('success');

  /** The DM's inbox is scoped to just the investigating officer's report-ready handoffs. */
  readonly isDm = computed(() => this.rbac.role() === 'dm');

  /**
   * Role-scoped inbox. The DM only needs the "investigation report ready" alerts
   * the record officer raises on Submit Report — those are the complaints ready
   * to register as a case — so every other handoff (assignment tracking, hearing
   * scheduled/heard, resolution echoes) is filtered out of their view.
   */
  readonly visible = computed(() => {
    const all = this.ops.notificationList();
    return this.isDm() ? all.filter((n) => isCaseReadyForDm(n)) : all;
  });

  /** Unread within the role-scoped view, so the header count matches what's shown. */
  readonly unreadCount = computed(() => this.visible().filter((n) => !n.read).length);

  readonly items = computed(() => {
    const cat = this.category();
    return this.visible()
      .filter((n) => {
        const unreadOk = this.filter() === 'All' || !n.read;
        const catOk = cat === 'All' || n.type === cat;
        return unreadOk && catOk;
      })
      .slice()
      .sort((a, b) => String(b.at).localeCompare(String(a.at)));
  });

  categoryCount(cat: AppNotification['type'] | 'All'): number {
    const all = this.visible();
    if (cat === 'All') return all.length;
    return all.filter((n) => n.type === cat).length;
  }

  markRead(id: string | number): void {
    void this.ops.markNotificationRead(id);
  }

  guidance(notification: AppNotification): NotificationGuidance | null {
    const raw = notificationGuidance(notification);
    if (!raw) return null;
    return remapGuidanceForRole(
      raw,
      (module) => this.rbac.canModule(module),
      notification.grievanceRef,
    );
  }

  async markAllRead(): Promise<void> {
    if (!this.unreadCount() || this.busy()) return;
    this.busy.set(true);
    this.message.set(null);
    try {
      if (this.isDm()) {
        // Only mark the report-ready alerts the DM actually sees, not the
        // whole underlying feed that read-all would touch.
        const unread = this.visible().filter((n) => !n.read);
        await Promise.all(unread.map((n) => this.ops.markNotificationRead(n.id)));
      } else {
        await this.ops.markAllNotificationsRead();
      }
      this.notify('All notifications marked as read.');
    } catch {
      this.notify('Could not mark all notifications as read.', 'error');
    } finally {
      this.busy.set(false);
    }
  }

  async deleteAll(): Promise<void> {
    if (this.busy()) return;
    const ids = this.visible().map((n) => n.id);
    if (!ids.length) return;
    const count = ids.length;
    const confirmed = window.confirm(
      `Delete all ${count} notification${count === 1 ? '' : 's'} from this list?`,
    );
    if (!confirmed) return;
    this.busy.set(true);
    try {
      await this.ops.deleteAllNotifications(ids);
      this.notify(
        count === 1 ? '1 notification deleted.' : `All ${count} notifications deleted.`,
      );
    } catch {
      this.notify('Could not delete the notifications.', 'error');
    } finally {
      this.busy.set(false);
    }
  }

  async openNextStep(notification: AppNotification): Promise<void> {
    const guidance = this.guidance(notification);
    if (!guidance) return;
    if (!notification.read) {
      try {
        await this.ops.markNotificationRead(notification.id);
      } catch {
        this.notify('Notification could not be marked as read.', 'error');
      }
    }
    await this.router.navigateByUrl(guidance.actionPath);
  }

  async remove(id: string | number): Promise<void> {
    try {
      await this.ops.deleteNotification(id);
      this.notify('Notification deleted.');
    } catch {
      this.notify('Could not delete the notification.', 'error');
    }
  }

  /** Re-set to null first so repeating the same text still re-triggers the toast. */
  private notify(text: string, tone: 'success' | 'error' = 'success'): void {
    this.messageTone.set(tone);
    this.message.set(null);
    this.message.set(text);
  }
}
